import { useQuery } from "@apollo/client";
import {
  Fab,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";
import EditIcon from "@material-ui/icons/Edit";
import { Skeleton } from "@material-ui/lab";
import moment from "moment";
import React from "react";
import { useParams } from "react-router";
import {
  GreenCheckbox,
  RedCheckbox,
  YellowCheckbox,
} from "../components/Checkbox";
import LoadingError from "../components/LoadingError";
import ResendDialog from "../components/Tests/ResendDialog";
import TestDialog from "../components/Tests/TestDialog";
import TextBox from "../components/TextBox";
import { IdentifierMapping } from "../constants";
import { GET_TEST } from "../graphql/queries";
import { GetTestQuery } from "../graphql/queries/getTest";
import { GetTestQueryVariables } from "../graphql/types";
interface Props {}
const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },

  contentRoot: {
    display: "flex",
    flex: 1,
    padding: "40px 60px",
    width: "100%",
    flexDirection: "column",
    marginTop: 40,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  rowWrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
  },
  row: {
    display: "flex",
    flex: 1,
    height: "95%",
    flexDirection: "column",
  },
  content: {
    paddingTop: "5mm",
    display: "flex",
    flex: 1,
    width: "100%",
    flexDirection: "column",
  },
  resultRoot: {
    display: "flex",
    alignItems: "center",
    marginTop: "40px",
    width: "100%",
  },
  resultWrapper: {
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    flex: 1,
  },
  signatureWrapper: {
    marginTop: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  spacing: {
    height: "40px",
  },
  signature: {
    width: "100mm",
    borderBottom: "1px solid gray",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  img: {
    width: "160px",
    height: "80px",
    position: "relative",
    top: 0,
  },
  imgSignature: {
    width: "160px",
    height: "80px",
    position: "relative",
    top: 0,
  },
}));
const TestDetail: React.FC<Props> = () => {
  const classes = useStyles();
  const [showDialog, setShowDialog] = React.useState<boolean>();
  const [showResendDialogDialog, setShowResendDialogDialog] =
    React.useState<boolean>();

  const {
    locationId = "",
    testId,
    date,
  } = useParams<{
    locationId: string;
    testId: string;
    date: string;
  }>();
  const { data, loading, error } = useQuery<
    GetTestQuery,
    GetTestQueryVariables
  >(GET_TEST, {
    fetchPolicy: "cache-first",
    variables: {
      date,
      testId,
      locationId,
    },
  });
  const { getTest: test } = data || {};
  const {
    company: { name: company = "" } = {},
    identifier = "insurance",
    location: { name: location = "" } = {},
    patient: {
      firstname = "",
      lastname = "",
      insuranceNumber = "",
      email = undefined,
    } = {},
    result,
    userId = "",
  } = test || {};
  const _handleCloseDialog = () => {
    setShowDialog(false);
  };
  const _openDialog = () => {
    setShowDialog(true);
  };
  const _handleCloseResendDialog = () => {
    setShowResendDialogDialog(false);
  };
  const _openResendDialog = () => {
    setShowResendDialogDialog(true);
  };
  return (
    <div className={classes.root}>
      <TestDialog
        open={!!showDialog}
        id={{ locationId, testId, date }}
        handleClose={_handleCloseDialog}
        initialValues={{
          identifier,
          insuranceNumber,
        }}
      />
      <ResendDialog
        open={!!showResendDialogDialog}
        id={{ locationId, testId, date }}
        handleClose={_handleCloseResendDialog}
        initialValues={{
          email,
        }}
      />
      <Grid container className={classes.header}>
        <Grid item xs={12} md={11}>
          <Typography variant="h3">
            {!loading && `Test ${testId} am ${moment(date).format("LLL")} Uhr`}
            {loading && <Skeleton variant="text" width={700} height={80} />}
          </Typography>
        </Grid>
        {!loading && (
          <Grid item xs={2} md={1} container justify="space-between">
            <Tooltip title="Standorte exportieren">
              <Fab
                aria-label="resend test email"
                size={"small"}
                disabled={loading}
                onClick={_openResendDialog}
                color={"primary"}
              >
                <EmailIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Testpersonendaten ändern">
              <Fab
                aria-label="update test"
                size={"small"}
                disabled={loading}
                onClick={_openDialog}
                color={"primary"}
              >
                <EditIcon />
              </Fab>
            </Tooltip>
          </Grid>
        )}
      </Grid>

      {!loading && !!error && (
        <LoadingError text="Der Test konnte nicht geladen werden " />
      )}
      {!error && loading && (
        <>
          <Skeleton variant="rect" height={600} style={{ marginTop: 20 }} />
        </>
      )}
      {!loading && !error && (
        <Paper className={classes.contentRoot}>
          <Grid container justify="center">
            <Grid container direction="row" spacing={8} item xs={11}>
              <Grid item xs={12} md={6}>
                <Typography variant="h4">Testabnahme</Typography>
                <div className={classes.content}>
                  <TextBox label="Auftraggeber" value={company} />
                  <TextBox label="Durchführungsort" value={location} />
                  <div className={classes.spacing} />
                  <TextBox
                    label="Entnahme-Datum"
                    value={moment(date).format("LL")}
                  />
                  <TextBox label="Benutzer" value={userId} />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4">Testperson</Typography>
                <div className={classes.content}>
                  <TextBox label="Nachname" value={lastname} />
                  <TextBox label="Vorname" value={firstname} />
                  <div className={classes.spacing} />

                  <TextBox
                    label={IdentifierMapping[identifier]}
                    value={insuranceNumber}
                  />
                  <TextBox
                    label="Auswertungs-Datum"
                    value={`${moment(date).format("LLL")} Uhr`}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid item xs={10}>
              <div className={classes.resultRoot}>
                <Typography variant="h6"> Testergebnis:</Typography>
                <div className={classes.resultWrapper}>
                  <FormControlLabel
                    value="start"
                    control={<GreenCheckbox checked={result === "negativ"} />}
                    label={<Typography variant="subtitle1">Negativ</Typography>}
                    labelPlacement="end"
                  />
                  <FormControlLabel
                    value="start"
                    control={<RedCheckbox checked={result === "positiv"} />}
                    label={<Typography variant="subtitle1">Positiv</Typography>}
                    labelPlacement="end"
                  />

                  <FormControlLabel
                    value="start"
                    control={<YellowCheckbox checked={result === "invalid"} />}
                    label={
                      <Typography variant="subtitle1">Ungültig</Typography>
                    }
                    labelPlacement="end"
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </Paper>
      )}
    </div>
  );
};

export default TestDetail;
